import { useCobaltLink } from "@cobalt-technology/react-link";
import { useActions, useCedarStore, lazyFindElement } from "cedar-ui";
import React, { useCallback, useEffect, useState } from "react";
import SideBarV2 from "../SideBarV2/SideBarV2";
import { getDoctorSpecialty, isCustomSpecialty } from "../Walkthrough";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import InformationModal from "./InformationModal";
import IntegrationModal from "./IntegrationModal";
import { checkConditionPeriodically, ifElementExists } from "cedar-ui";
import UploadMacrosPanel from "./SettingsMacros/UploadMacros";
import ManageMacrosPanel from "./SettingsMacros/ManageMacros";
import { useContext } from "react";
import { MacrosContext } from "../../utils/MacrosContext";

export default function Settings({ setAuth, setIsSubscribed, isSubscribed }) {
  const [userName, setUserName] = useState();
  const [userId, setUserId] = useState();
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [formattedSubEndDate, setFormattedSubEndDate] = useState(null);
  const [linkToken, setLinkToken] = useState(null);
  const [publicToken, setPublicToken] = useState(""); // State to store public token
  const [isReadyToInit, setIsReadyToInit] = useState(false);
  const [buttonStatus, setButtonStatus] = useState(false); // Use this state to control the button text
  const [accessToken, setAccessToken] = useState(null);
  const [org, setOrg] = useState();
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [specialty, setSpecialty] = useState(null);
  const [superOrg, setSuperOrg] = useState(null);
  const [isIntegrated, setIsIntegrated] = useState("");

  const [activeTab, setActiveTab] = useState("upload");

  const openSidebar = () => setIsSidebarOpen(true);
  const closeSidebar = () => setIsSidebarOpen(false);
  const { removeAction, addActionsToStart } = useActions();

  //macros context
  const { userMacros, setUserMacros, fetchUserMacros } =
    useContext(MacrosContext);

  // Get Cedar store functions to control button visibility
  const { setShowButton, currentAction } = useCedarStore();

  useEffect(() => {
    fetchUserMacros();
  }, []);

  useEffect(() => {
    if (showIntegrationModal && currentAction) {
      addActionsToStart([
        {
          type: "IDLE",
          advanceFunction: async (nextAction) => {
            // Use checkConditionPeriodically to wait for the element
            const conditionMet = await checkConditionPeriodically(
              // The condition to check
              ifElementExists(
                lazyFindElement({ text: "Live Chat" }, { x: 800, y: 500 })
              )
            );

            if (conditionMet) {
              nextAction();
            }
          },
          blocking: true,
        },
        {
          type: "VIRTUAL_CLICK",
          endPosition: lazyFindElement(
            { text: "Live Chat" },
            { x: 800, y: 500 }
          ),
          tooltipText: "Click here to start a live chat",
          blocking: true,
        },
        {
          type: "VIRTUAL_TYPING",
          endPosition: lazyFindElement({
            selector: "textarea", // Targeting the textarea by its placeholder
          }),
          expectedValue: "integration doesn't work for me",
          typingDelay: 50,
        },
        {
          type: "DIALOGUE",
          text: "Please submit the chat feedback and we'll continue the integration walkthrough!",
        },
        {
          type: "EXECUTE_CLICK",
          target: lazyFindElement(
            "button.text-2xl.absolute.top-3.right-5.font-extrabold"
          ),
          showCursor: false,
        },
      ]);
      removeAction("integration-dialogue");
      removeAction("checkCobaltIdle");
    }
  }, [showIntegrationModal]);

  // Fetch user info on component mount
  useEffect(() => {
    async function getUserInfo() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/dashboard/userinfo`,
          {
            method: "GET",
            headers: { token: localStorage.token },
          }
        );

        const parseRes = await response.json();
        setUserName(parseRes[0].user_name);
        setUserId(parseRes[0].user_id);
        setOrg(parseRes[0].organization);
        setSubscriptionId(parseRes[0].subscription_id);
        setSubscriptionEndDate(parseRes[0].subscription_end_date);
        setSubscriptionId(parseRes[0].subscription_id);
        setSubscriptionEndDate(parseRes[0].subscription_end_date);
        setSuperOrg(parseRes[0].super_org);
        setIsIntegrated(parseRes[0].ehr_password);
      } catch (err) {
        console.error("Error fetching user info:", err.message);
      }
    }
    getUserInfo();

    // Check if access token exists
    async function checkExistingIntegration() {
      try {
        const storedToken = await getStoredAccessToken();
        if (storedToken) {
          setAccessToken(storedToken);
        }
      } catch (error) {
        console.error("Error checking existing integration:", error);
      }
    }
    checkExistingIntegration();
  }, []);

  useEffect(() => {
    if (accessToken && currentAction) {
      removeAction("integration-button");
      removeAction("integration-dialogue");
    }
  }, [accessToken]);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/subscription-status`,
          {
            method: "GET",
            headers: { token: localStorage.token },
          }
        );

        const parseRes = await response.json();
        setIsSubscribed(parseRes.is_subscribed);
      } catch (err) {
        console.error("Error fetching user info:", err.message);
      }
    })();
  }, [isSubscribed]);

  const handleOpenCancelClick = () => {
    setShowCancellationModal(true);
  };

  const handleOpenInformationClick = () => {
    setShowInformationModal(true);
  };

  useEffect(() => {
    if (subscriptionEndDate) {
      const options = { year: "numeric", month: "2-digit", day: "2-digit" };
      const formattedDate = new Date(subscriptionEndDate).toLocaleDateString(
        "en-US",
        options
      );
      setFormattedSubEndDate(formattedDate);
      localStorage.setItem("subscription_ends", formattedDate);
    }
  }, [subscriptionEndDate]);

  const getPublicToken = async () => {
    if (!userId) {
      console.error("User ID is not available.");
      return;
    }

    setButtonStatus(true); // Start loading before the fetch request

    //TO-DO: NEED TO STORE COBALT ID AND SECRET IN ENV VARIABLES
    try {
      const response = await fetch(
        "https://api.usecobalt.com/link/token/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            client_id: "ci_live_RRG6m7sZGBWod2xfrhRkw4Ph",
            client_secret: "cs_live_iwoQAkjVRhao7xRbWLLdDUnu",
          },
          body: JSON.stringify({ user_id: userId, org_id: org }),
        }
      );

      if (response.status === 403 || response.status === 409) {
        console.log("Error data  " + response.status);
        console.error("Account link expired. Relinking...");
        const storedAccessToken = await getStoredAccessToken();
        if (storedAccessToken) {
          await relinkAccount(storedAccessToken);
        } else {
          console.error("Unable to get stored access token.");
        }
      } else {
        const data = await response.json();
        if (data && data.token) {
          setLinkToken(data.token);
          setIsReadyToInit(true); // Set ready to initialize
        } else {
          console.error("Failed to obtain link token:", data.message);
          setShowIntegrationModal(true);
          setButtonStatus(false);
          // alert(
          //   "Unable to setup your integration at this time. Please try again or contact Knowtex support. Error details: " +
          //     data.message
          // );
        }
      }
    } catch (err) {
      console.error("Failed to setup integration:", err);
      setShowIntegrationModal(true);
      setButtonStatus(false);
      // alert(
      //   "Unable to setup your integration at this time. Please try again or contact Knowtex support. Error details: " +
      //     err.message
      // );
    } finally {
      setButtonStatus(false); // Stop loading after the fetch request
    }
  };

  const getStoredAccessToken = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/getaccesstoken`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            token: localStorage.getItem("token"),
          },
        }
      );

      if (!response.ok) {
        console.log("Can't get access token " + response);
        alert(
          "Unable to verify credentials to get access token. Please try again or contact Knowtex support to resolve this"
        );
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      if (data.accessToken) {
        //console.log("Access token " + data.accessToken);
        return data.accessToken;
      } else {
        console.error("Access token not available.");
        return null;
      }
    } catch (error) {
      console.error("Failed to fetch access token:", error);
      return null;
    }
  };

  const refreshUserData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/userinfo`,
        {
          method: "GET",
          headers: { token: localStorage.token },
        }
      );

      const parseRes = await response.json();
      setUserName(parseRes[0].user_name);
      setUserId(parseRes[0].user_id);
      setOrg(parseRes[0].organization);
      setSubscriptionId(parseRes[0].subscription_id);
      setSubscriptionEndDate(parseRes[0].subscription_end_date);
      setSuperOrg(parseRes[0].super_org);
      setIsIntegrated(parseRes[0].ehr_password);
    } catch (err) {
      console.error("Error fetching user info:", err.message);
    }
  };

  const relinkAccount = async (storedAccessToken) => {
    try {
      const relinkResponse = await fetch(
        "https://api.usecobalt.com/link/token/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            client_id: "ci_live_RRG6m7sZGBWod2xfrhRkw4Ph",
            client_secret: "cs_live_iwoQAkjVRhao7xRbWLLdDUnu",
          },
          body: JSON.stringify({
            user_id: userId,
            org_id: org,
            access_token: storedAccessToken,
          }),
        }
      );

      const relinkData = await relinkResponse.json();
      if (relinkData && relinkData.token) {
        setLinkToken(relinkData.token);
        setIsReadyToInit(true); // Set ready to initialize
      } else {
        console.error("Failed to relink account:", relinkData.message);
        alert(
          "Unable to setup your integration at this time. Please try again or contact Knowtex support. Error details: " +
            relinkData.message
        );
      }
    } catch (err) {
      console.error("Failed to relink account:", err);
      alert(
        "Unable to setup your integration at this time. Please try again or contact Knowtex support. Error details: " +
          err.message
      );
    } finally {
      setButtonStatus(false); // Stop loading after the fetch request
    }
  };

  /*
    Begin CobaltLink Code
  */

  // Setup the onSuccess callback for CobaltLink
  const onSuccess = useCallback((public_token) => {
    //console.log("got the public_token: ", public_token);
    setPublicToken(public_token);
    setButtonStatus(false);

    //Exchange Public token for Access token
    getAccessToken(public_token);
  }, []);

  // Setup the Cobalt Link with the token
  const { init, error, loading } = useCobaltLink({
    token: linkToken,
    onSuccess,
  });

  useEffect(() => {
    if (isReadyToInit && linkToken && !publicToken) {
      init();
    }
  }, [isReadyToInit, linkToken, init]);

  /*
    End CobaltLink Code
  */

  const getAccessToken = async (pToken) => {
    try {
      const response = await fetch(
        `https://api.usecobalt.com/link/token/exchange?public_token=${encodeURIComponent(
          pToken
        )}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            client_id: "ci_live_RRG6m7sZGBWod2xfrhRkw4Ph",
            client_secret: "cs_live_iwoQAkjVRhao7xRbWLLdDUnu",
          },
        }
      );
      const data = await response.json();
      if (data && data.token) {
        console.log("got the access token");
        setAccessToken(data.token);

        // Insert access token into postgres db
        try {
          const body = { accessToken: data.token };
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/dashboard/insertaccesstoken`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                token: localStorage.token,
              },
              body: JSON.stringify(body),
            }
          );

          const parseRes = await response.json();
          console.log(parseRes);
        } catch (err) {
          console.error("Error inserting access token into db:", err.message);
          setShowIntegrationModal(true);
          setButtonStatus(false);
          // alert(
          //   "Unable to setup your integration at this time. Please try again or contact Knowtex support. Error details: " +
          //   err.message
          // );
        }
      } else {
        console.error("Failed to obtain access token:", data.message);
        setShowIntegrationModal(true);
        setButtonStatus(false);
        // alert(
        //   "Unable to setup your integration at this time. Please try again or contact Knowtex support. Error details: " +
        //   data.message
        // );
      }
    } catch (err) {
      console.error("Failed to setup integration:", err);
      setShowIntegrationModal(true);
      setButtonStatus(false);
    }
  };

  // Fetch doctor specialty on component mount
  useEffect(() => {
    async function fetchSpecialty() {
      const doctorSpecialty = await getDoctorSpecialty();
      setSpecialty(doctorSpecialty);
    }

    fetchSpecialty();
  }, []);

  // Control button visibility based on specialty and tutorial status
  useEffect(() => {
    // Show button if:
    // 1. The specialty qualifies for walkthrough
    // 2. There's a current action (tutorial is active)
    if (specialty && isCustomSpecialty(specialty) && currentAction) {
      setShowButton(true);
    }

    // Cleanup on unmount
    return () => {
      setShowButton(false);
    };
  }, [specialty, currentAction, setShowButton]);

  return (
    <div className="flex flex-col">
      <SideBarV2
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        setAuth={setAuth}
      />
      <button
        onClick={openSidebar}
        className="text-5xl text-accent2 text-left ml-10 mt-10 rounded desktop:hidden"
      >
        ☰
      </button>
      <div className="flex flex-col transition-margin ml-10 tablet:mt-3 desktop:ml-dashboard-left">
        <div className="flex flex-row items-center mt-5 text-4xl font-bold text-accent1 border-b-2 border-transparent-purple w-screen -mr-10 pr-10">
          <svg
            className="w-12 h-12 text-accent1"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="square"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M10 19H5a1 1 0 0 1-1-1v-1a3 3 0 0 1 3-3h2m10 1a3 3 0 0 1-3 3m3-3a3 3 0 0 0-3-3m3 3h1m-4 3a3 3 0 0 1-3-3m3 3v1m-3-4a3 3 0 0 1 3-3m-3 3h-1m4-3v-1m-2.121 1.879-.707-.707m5.656 5.656-.707-.707m-4.242 0-.707.707m5.656-5.656-.707.707M12 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>
          <h1 className="p-1 ">Settings</h1>
        </div>
        <div className="flex mt-5 text-2xl font-bold text-accent1">
          <h2>{userName}'s Integrations</h2>
        </div>
        <div className="mt-3 mb-3">
          {superOrg !== "TenOnc" && superOrg !== "Ironwood" ? (
            <>
              <button
                onClick={getPublicToken}
                disabled={loading || accessToken}
                className={`px-4 py-2 ${
                  accessToken ? "bg-green-500" : "bg-accent2"
                } text-white rounded hover:${
                  accessToken ? "bg-green-600" : "bg-accent1"
                }`}
              >
                {buttonStatus
                  ? "Loading..."
                  : accessToken
                  ? "✓ Integration Linked"
                  : "Set Up Integration"}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setShowIntegrationModal(true)}
                disabled={loading || (accessToken && isIntegrated)}
                className={`px-4 py-2 ${
                  accessToken && isIntegrated ? "bg-green-500" : "bg-accent2"
                } text-white rounded hover:${
                  accessToken && isIntegrated ? "bg-green-600" : "bg-accent1"
                }`}
              >
                {buttonStatus
                  ? "Loading..."
                  : accessToken && isIntegrated
                  ? "✓ Integration Linked"
                  : "Set Up Integration"}
              </button>
            </>
          )}
        </div>
        {accessToken &&
          (superOrg !== "TenOnc" && superOrg !== "Ironwood" ? (
            <>
              <div className="mt-5 font-bold">
                <h2 className="text-accent1 text-2xl">Relink Integrations </h2>
                <div className="text-light-grey italic text-sm">
                  Set up integration if you've changed your OncoEMR password or
                  need to relink Integrations
                </div>
              </div>
              <div
                className="mt-3 mb-3"
                onClick={getPublicToken}
                disabled={loading}
              >
                <button
                  className={`px-4 py-2 bg-accent2 text-white rounded hover:bg-accent1`}
                >
                  {buttonStatus ? "Loading..." : "Set Up Integration"}
                </button>
              </div>
            </>
          ) : (
            isIntegrated && (
              <>
                <div className="mt-5 font-bold">
                  <h2 className="text-accent1 text-2xl">
                    Relink Integrations{" "}
                  </h2>
                  <div>
                    Set up integration if you've changed your OncoEMR password
                    or need to relink Integrations
                  </div>
                </div>
                <div className="mt-3 mb-3">
                  <button
                    className={`px-4 py-2 bg-accent2 text-white rounded hover:bg-accent1`}
                    onClick={() => setShowIntegrationModal(true)}
                    disabled={loading}
                  >
                    {buttonStatus ? "Loading..." : "Set Up Integration"}
                  </button>
                </div>
              </>
            )
          ))}
        {showIntegrationModal && (
          <IntegrationModal
            setShowIntegrationModal={setShowIntegrationModal}
            superOrg={superOrg}
            accessToken={accessToken}
            userId={userId}
            onSuccessfulIntegration={refreshUserData}
          />
        )}
        <div>
          {subscriptionId && (
            <button
              onClick={handleOpenCancelClick}
              disabled={loading}
              className="px-4 py-2 bg-accent2 text-white rounded hover:bg-accent1"
            >
              {buttonStatus ? "Loading..." : "Cancel Subscription"}
            </button>
          )}
        </div>
        {showCancellationModal ? (
          <CancelSubscriptionModal
            userId={userId}
            subscriptionId={subscriptionId}
            setSubscriptionId={setSubscriptionId}
            setShowCancellationModal={setShowCancellationModal}
            setShowInformationModal={setShowInformationModal}
          />
        ) : null}
        {!subscriptionId && subscriptionEndDate && (
          <div className="mt-3 mb-3">
            <button
              onClick={handleOpenInformationClick}
              disabled={loading}
              className="px-4 py-2 bg-accent2 text-white rounded hover:bg-accent1"
            >
              {buttonStatus ? "Loading..." : "Subscription Info"}
            </button>
          </div>
        )}
        {showInformationModal && (
          <InformationModal
            setShowInformationModal={setShowInformationModal}
            subscriptionEndDate={formattedSubEndDate}
          />
        )}

        {/* Macros Section with Tabs */}
        <div className="flex flex-col mt-5 mb-5">
          <h2 className="text-accent1 text-2xl font-bold ">Macros</h2>
          <div className="text-light-grey italic text-sm mb-4">
            View and manage your macros.
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("upload")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "upload"
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Upload Custom Macros
              </button>
              <button
                onClick={() => setActiveTab("manage")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "manage"
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                My Macros
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="max-w-4xl w-full">
            {activeTab === "upload" && <UploadMacrosPanel userId={userId} />}
            {activeTab === "manage" && <ManageMacrosPanel />}
          </div>
        </div>
      </div>
    </div>
  );
}
